import { Grid, Typography } from '@material-ui/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import BaseButton from 'src/common/components/base-button';
import { DialogBar } from 'src/common/components/interactive-dialogue/components/interactive-dialog-content/interactive-dialog-content';
import { IKeypadModal, KeypadModal } from 'src/common/components/keypad-modal/keypad-modal';
import { pushEmeraldModal } from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import CashierPreferencesHelper from 'src/common/helpers/cashier-preferences-helper';
import { IButton } from 'src/common/interfaces/IButton';
import { ScannedDataHandler } from 'src/devices/scanner-operations';
import Keypad from 'src/keypad/components/keypad';
import { RifmOptions } from 'src/keypad/keypad-operation';
import { styles } from './age-restiction-styles';
import barcode from 'src/item-list/media/barcode.svg';
import { ReactComponent as TransactionsDoB } from 'src/common/media/TransactionsDoB.svg';
import { DividerText } from './dob-approve-reject-age-restriction-modal';

type Props = IAgeRestrictionActionsProps;

export const showDateOfBirthAgeRestrictionDialog = (
  props: Props,
  onDataScanned: ScannedDataHandler,
) => {
  pushEmeraldModal(<DateOfBirthAgeRestrictionModal {...props} />, onDataScanned);
};

// Trigger with code 11011 or item Toshiba TV 14af43. If not working, check/add policy in CCM
const DateOfBirthAgeRestrictionModal: React.FC<IAgeRestrictionActionsProps> = ({
  onClose,
  onVerify,
  errorMessage,
  expectedAge,
}): JSX.Element => {
  const classes = styles();
  const { t } = useTranslation();
  const isLeftHanded = CashierPreferencesHelper.isLeftHanded();

  const keypadSubmitButton: IButton = {
    label: 'Verify Age',
    onClick: onVerify,
  };

  const finalErrorMessage = expectedAge
    ? t('CustomerAgeRestriction', { arg: expectedAge })
    : t(errorMessage ?? '');

  const keypadModalProps: IKeypadModal = {
    onClose: onClose,
    title: 'AgeRestriction_Title',
    buttonComponent: (
      <Grid item>
        <BaseButton
          buttonType='white'
          className={classes.cancelButton}
          style={isLeftHanded ? { marginLeft: '1.9vw' } : { marginRight: '1.9vw' }}
          data-testid='cancel-button-age-restriction'
          onClick={onClose}
        >
          <span className='label'>{t('Cancel')}</span>
        </BaseButton>
      </Grid>
    ),
    iconType: EmeraldDialogTypes.Warning,
    contentArea: (
      <Grid className={isLeftHanded ? classes.innerInverse : classes.inner}>
        <Grid item>
          <Typography data-testid='scan-drivers-license-title-text' variant='h2' align='center'>
            {t('AgeRestriction_Verify')}
          </Typography>
        </Grid>
        <Grid item>
          <DialogBar type={EmeraldDialogTypes.Error} />
        </Grid>
        <Grid
          item
          data-testid='check-id-instruction-text-line-1-container'
          className={classes.checkIdInstructionWrapper}
        >
          <Typography className={classes.checkIdInstructionText} variant='body1'>
            {finalErrorMessage}
          </Typography>
        </Grid>
        <Grid item data-testid='check-id-instruction-text-line-2-container' alignItems='center'>
          <Grid container direction='column'>
            <Grid item className={classes.scanDriversLicenseContainer}>
              <Grid container>
                <Grid item className={classes.paddingIcon}>
                  <img src={barcode} alt='Barcode' />
                </Grid>
                <Grid item>
                  <Typography className={classes.scanDriversLicenseText} variant='body1'>
                    {t('ScanDriversLicense')}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item className={classes.dividerContainer}>
              <DividerText text={t('CustomerAgeVerification_Or')}></DividerText>
            </Grid>
            <Grid item className={classes.enterDateManually}>
              <Grid container>
                <Grid item className={classes.paddingIcon}>
                  <TransactionsDoB />
                </Grid>
                <Grid item>
                  <Typography variant='body1' className={classes.scanDriversLicenseText}>
                    {t('AgeRestriction_EnterDobManually')}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    ),
    keypad: (
      <Keypad
        disableManualWeight={true}
        disableQuantity={true}
        disableDecimalInput={true}
        keyboardId={'date-of-birth-restriction'}
        minHeight={'27.3vh'}
        visible={true}
        topLabelText={''}
        inputInstructionsLabelText={'MM/DD/YYYY'}
        inputFieldText={t('DateofBirth')}
        keypadTestIdPrefix={'input-age-restriction-'}
        submitButton={keypadSubmitButton}
        formatter={RifmOptions.date}
      />
    ),
    modalWidth: 62.22,
    modalHeight: 80.1,
  };

  return <KeypadModal {...keypadModalProps} />;
};

interface IAgeRestrictionActionsProps {
  onClose: () => void;
  onVerify: (verificationData: string) => void;
  expectedAge?: string;
  errorMessage?: string;
}

export default DateOfBirthAgeRestrictionModal;
